import { MouseEvent, SyntheticEvent, useCallback, useId, useRef, useState } from 'react';
import { ButtonBaseProps, PopoverProps } from '@evaneos/b2b-ui';

type OriginV = 'top' | 'bottom';
type OriginH = 'left' | 'right';

type PlacementConfig = {
    anchorOrigin?: { vertical: OriginV; horizontal: OriginH };
    transformOrigin?: { vertical: OriginV; horizontal: OriginH };
};

export function useEditPopover(
    labelId: string,
    baseId?: string,
    onBeforeClose?: () => boolean | void,
) {
    const generatedId = useId();
    const id = baseId ?? generatedId;

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [triggerWidth, setTriggerWidth] = useState<number>(0);
    const [triggerHeight, setTriggerHeight] = useState<number>(0);
    const open = Boolean(anchorEl);
    const popoverId = open ? `${id}-popover` : undefined;
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const close = useCallback(() => {
        // Call onBeforeClose if provided. If it returns false, prevent closing.
        if (onBeforeClose) {
            const result = onBeforeClose();
            if (result === false) {
                return;
            }
        }

        setAnchorEl(null);
        triggerRef.current?.focus();
    }, [onBeforeClose]);

    const getTriggerProps = useCallback(
        (userProps: Partial<ButtonBaseProps> = {}) => ({
            ref: triggerRef,
            type: 'button' as const,
            id: `${id}-trigger`,
            'aria-haspopup': 'dialog' as const,
            'aria-labelledby': `${labelId} ${id}-trigger`,
            'aria-expanded': open || undefined,
            'aria-controls': popoverId,
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                userProps.onClick?.(e);
                const { width, height } = e.currentTarget.getBoundingClientRect();
                setTriggerWidth(width);
                setTriggerHeight(height);
                setAnchorEl(e.currentTarget);
            },
            ...userProps,
        }),
        [id, labelId, open, popoverId],
    );

    const getPopoverProps = useCallback(
        (userProps: Partial<PopoverProps> = {}, placement?: PlacementConfig) => ({
            id: popoverId,
            open,
            anchorEl,
            transitionDuration: { enter: 225, exit: 0 },
            onClose: (event: SyntheticEvent, reason: 'backdropClick' | 'escapeKeyDown') => {
                userProps.onClose?.(event, reason);
                close();
            },
            anchorOrigin: placement?.anchorOrigin ?? {
                vertical: 'bottom',
                horizontal: 'left',
            },
            transformOrigin: placement?.transformOrigin ?? {
                vertical: 'top',
                horizontal: 'left',
            },
            slotProps: {
                paper: {
                    role: 'dialog',
                    'aria-labelledby': labelId,
                    ...(userProps.slotProps?.paper || {}),
                },
            },
            ...userProps,
        }),
        [popoverId, open, anchorEl, close, labelId],
    );

    return { open, getTriggerProps, getPopoverProps, close, triggerWidth, triggerHeight, anchorEl };
}
